import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { getWishlistItems } from "@/utils/wishlistStorage";
import { getCartItems } from "@/utils/cartStorage";

const AuthContext = createContext();
const BASE_API_URL = "/api/proxy";

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const saveAuth = (authData) => {
    if (authData) {
      const token = authData.token || authData.access_token;
      localStorage.setItem("accessToken", token);
      setAuth({ accessToken: token });
      setAccessToken(token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenExpiry");
      setAuth(null);
      setAccessToken(null);
      delete axios.defaults.headers.common["Authorization"];
    }
  };

  // Fungsi untuk logout
  const logout = () => {
    saveAuth(null);
    setCurrentUser(null);
    
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  const refetchUser = async () => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      console.warn("Access token tidak ditemukan. Tidak fetch user.");
      return null;
    }

    try {
      const response = await axios.get("/api/profile", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        validateStatus: (status) => status < 500,
      });

      const user = response.data?.data || response.data;

      if (response.status === 200 && user?.uuid) {
        setCurrentUser(user);
        return user;
      } else {
        throw new Error("Data pengguna tidak ditemukan di respons");
      }
    } catch (error) {
      console.error("Gagal mengambil data pengguna:", error);
      logout();
      return null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const expiry = localStorage.getItem("tokenExpiry");
    const isExpired = expiry && Date.now() > parseInt(expiry, 10);

    if (token && !isExpired) {
      setAuth({ accessToken: token });
      setAccessToken(token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      refetchUser().finally(() => {
        setIsAuthReady(true);
      });
    } else {
      if (isExpired) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiry");
      }
      setIsAuthReady(true);
    }
  }, []);

  // Sync wishlist localStorage → API saat user sudah login (add-only)
  useEffect(() => {
    if (!isAuthReady) return;
    if (!currentUser || !accessToken) return;

    let debounceTimer = null;
    let inFlight = false;

    const syncAdds = async () => {
      if (inFlight) return;
      const localItems = getWishlistItems();
      if (!Array.isArray(localItems) || localItems.length === 0) return;

      inFlight = true;
      try {
        // Ambil server wishlist untuk dedupe
        const serverRes = await axios.get("/api/wishlist", {
          headers: { Authorization: `Bearer ${accessToken}` },
          validateStatus: (s) => s < 500,
        });
        const serverData =
          serverRes.status === 200 ? serverRes.data?.data || [] : [];
        const serverKeys = new Set(
          serverData.map((w) => {
            const p =
              w?.product?.uuid ||
              w?.product_uuid ||
              w?.product?.id ||
              w?.product_id;
            const v =
              w?.variant?.uuid ||
              w?.product_variant_uuid ||
              w?.variant_uuid ||
              w?.variant?.id ||
              w?.product_variant_id ||
              "";
            return `${p || ""}::${v || ""}`;
          }),
        );

        for (const item of localItems) {
          const productUuid =
            item?.productData?.uuid ||
            item?.productData?.product_uuid ||
            item?.productUuid ||
            null;
          const variantUuid =
            item?.variantData?.uuid ||
            item?.variantData?.product_variant_uuid ||
            item?.variantData?.variant_uuid ||
            item?.variantUuid ||
            null;
          if (!productUuid) continue;

          const key = `${productUuid}::${variantUuid || ""}`;
          if (serverKeys.has(key)) continue;

          const payload = {
            product_uuid: productUuid,
            ...(variantUuid ? { product_variant_uuid: variantUuid } : {}),
          };

          const postRes = await axios.post("/api/wishlist", payload, {
            headers: { Authorization: `Bearer ${accessToken}` },
            validateStatus: (s) => s < 500,
          });
          if (postRes.status >= 200 && postRes.status < 300) {
            serverKeys.add(key);
          }
        }
      } catch {
        // silent
      } finally {
        inFlight = false;
      }
    };

    const scheduleSync = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        syncAdds();
      }, 400);
    };

    // initial sync saat login/refresh
    scheduleSync();

    window.addEventListener("basestore:wishlist-updated", scheduleSync);
    return () => {
      window.removeEventListener("basestore:wishlist-updated", scheduleSync);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [accessToken, currentUser, isAuthReady]);

  // Sync cart: GET dulu, hanya POST item yang belum ada di server (hindari duplikasi)
  useEffect(() => {
    if (!isAuthReady) return;
    if (!currentUser || !accessToken) return;

    let inFlight = false;

    const syncCartOnce = async () => {
      if (inFlight) return;
      const localItems = getCartItems();
      if (!Array.isArray(localItems) || localItems.length === 0) return;

      inFlight = true;
      try {
        // 1. GET cart dulu untuk cek apa yang sudah ada di server
        const serverRes = await axios.get("/api/cart", {
          headers: { Authorization: `Bearer ${accessToken}` },
          validateStatus: (s) => s < 500,
        });

        const rawData =
          serverRes.status === 200 ? serverRes.data?.data : null;

        // Normalisasi: dapatkan array line items dari berbagai bentuk response API
        let serverLineItems = [];
        if (Array.isArray(rawData)) {
          // Bentuk: data = [ { items: [...], total, total_items }, ... ]
          serverLineItems = rawData.flatMap((c) => c?.items ?? []);
        } else if (rawData && Array.isArray(rawData.items)) {
          // Bentuk: data = { items: [...], total, total_items }
          serverLineItems = rawData.items;
        }

        const serverKeys = new Set(
          serverLineItems.map((item) => {
            const p =
              item?.product?.uuid ||
              item?.product_uuid ||
              item?.product?.id ||
              item?.product_id;
            const v =
              item?.variant?.uuid ||
              item?.product_variant_uuid ||
              item?.variant_uuid ||
              item?.variant?.id ||
              item?.product_variant_id ||
              "";
            return `${p || ""}::${v || ""}`;
          }),
        );

        // 2. Hanya POST item lokal yang belum ada di server (jika sudah sama, tidak sync ulang)
        for (const item of localItems) {
          const productUuid =
            item?.productData?.uuid ||
            item?.productData?.product_uuid ||
            item?.productUuid ||
            null;
          const variantUuid =
            item?.variantData?.uuid || item?.variantUuid || null;
          if (!productUuid) continue;

          const key = `${productUuid}::${variantUuid || ""}`;
          if (serverKeys.has(key)) continue;

          const payload = {
            product_uuid: productUuid,
            ...(variantUuid ? { product_variant_uuid: variantUuid } : {}),
            quantity: item.quantity || 1,
          };

          const postRes = await axios.post("/api/cart", payload, {
            headers: { Authorization: `Bearer ${accessToken}` },
            validateStatus: (s) => s < 500,
          });
          if (postRes.status >= 200 && postRes.status < 300) {
            serverKeys.add(key);
          }
        }
      } catch {
        // silent
      } finally {
        inFlight = false;
      }
    };

    syncCartOnce();
  }, [accessToken, currentUser, isAuthReady]);

  return (
    <AuthContext.Provider
      value={{
        auth,
        accessToken,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        refetchUser,
        isAuthReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
